import { Component, Input, OnInit, signal } from '@angular/core';

/**
 * Global application footer (Hudson Insurance Group).
 *
 * Reused across every screen via the app shell. Shows copyright, quick links,
 * and a copyable session tag (optional client IP + the load timestamp).
 */
@Component({
  selector: 'app-footer',
  standalone: false,
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss'
})
export class FooterComponent implements OnInit {
  @Input() companyName = 'Hudson Insurance Group';
  @Input() companyUrl = 'https://www.hudsoninsgroup.com';
  @Input() resourcesUrl = '#';
  @Input() legalUrl = '#';
  @Input() supportUrl = '#';

  /**
   * Client/session IP shown in the session tag. Leave blank to show only the
   * timestamp; populate from a session/diagnostics service when available.
   */
  @Input() sessionIp = '';

  /** Copyright year, resolved once at construction. */
  readonly year = new Date().getFullYear();

  /** The formatted session tag ("<ip> - MM/DD/YYYY - h:mm AM/PM"). */
  readonly sessionTag = signal('');

  /** Toggles the transient "Copied" confirmation on the copy control. */
  readonly copied = signal(false);

  ngOnInit(): void {
    this.sessionTag.set(this.buildSessionTag());
  }

  private buildSessionTag(): string {
    const now = new Date();
    const date = now.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
    const time = now.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    const ip = this.sessionIp?.trim();
    return ip ? `${ip} - ${date} - ${time}` : `${date} - ${time}`;
  }

  /** Copies the session tag to the clipboard and briefly confirms. */
  async copySessionTag(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.sessionTag());
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 1500);
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — fail silently.
    }
  }
}
